import { useTranslation, t } from '../../../shared/i18n';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Badge, Button, Dropdown, Empty, Grid, Input, message, Modal, Select, Space, Spin, Table, Tabs, Tag,
  Tooltip, Typography, theme,
} from 'antd';
import {
  ArrowLeftOutlined, CaretRightOutlined, DeleteOutlined, PlusOutlined, RocketOutlined, SnippetsOutlined,
} from '@ant-design/icons';
import Editor from '@monaco-editor/react';
import cdnService from '../../services/cdnService';
import { userPath } from '../../../shared/config/paths';
import { useTheme } from '../../../theme/ThemeContext';
import { WORKER_TEMPLATES } from '../../../shared/constants/workerTemplates';

const { Text } = Typography;
const { useBreakpoint } = Grid;

const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];
const LOG_COLOR = { error: 'red', warn: 'orange', info: 'blue', debug: 'default', log: 'default' };

function decodeBody(b64) {
  if (!b64) return '';
  try {
    const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch (e) {
    return t('[Binary content {{length}} bytes base64]', { length: b64.length });
  }
}

function pretty(text, headers) {
  const ct = (headers || []).find(([k]) => k.toLowerCase() === 'content-type')?.[1] || '';
  if (ct.includes('json') || /^\s*[[{]/.test(text)) {
    try { return JSON.stringify(JSON.parse(text), null, 2); } catch (e) { /* not JSON */ }
  }
  return text;
}

/**
 * Cloudflare-style editor: code on the left, a live test panel on the right
 * (runs the code with the edge's own runtime via /cdn/workers/preview, so
 * env vars, limits and SSRF rules match production). Nothing reaches the edge
 * until "部署" creates a new version.
 */
export default function WorkerEditor() {
  const { t } = useTranslation();
  const { workerId } = useParams();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isWide = !!screens.lg;
  const { token } = theme.useToken();
  const { themeMode } = useTheme();

  const [worker, setWorker] = useState(null);
  const [code, setCode] = useState('');
  const [routes, setRoutes] = useState([]);
  const [deployOpen, setDeployOpen] = useState(false);
  const [deployMsg, setDeployMsg] = useState('');
  const [deploying, setDeploying] = useState(false);

  const [method, setMethod] = useState('GET');
  const [url, setUrl] = useState('https://example.com/');
  const [headers, setHeaders] = useState([{ k: 'user-agent', v: 'Mozilla/5.0 (Yeap Preview)' }]);
  const [body, setBody] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [resultTab, setResultTab] = useState('body');

  const dirty = worker && code !== (worker.code || '');
  const runRef = useRef(null);
  const deployRef = useRef(null);

  useEffect(() => {
    (async () => {
      const [w, r] = await Promise.all([cdnService.getWorker(workerId), cdnService.getWorkerRoutes(workerId)]);
      if (!w.success) {
        message.error(w.error || t('Failed to load Worker'));
        navigate(userPath('/cdn/workers'));
        return;
      }
      setWorker(w.data);
      setCode(w.data.code || '');
      const rs = r.success ? r.data || [] : [];
      setRoutes(rs);
      if (rs[0]) {
        // Host routes (api.example.com/x, *.example.com/*): use the host when concrete, keep only the path part.
        const pat = rs[0].route_pattern.replace(/^https?:\/\//i, '');
        const slash = pat.startsWith('/') ? 0 : pat.indexOf('/');
        const hostPart = pat.startsWith('/') ? '' : (slash === -1 ? pat : pat.slice(0, slash));
        const host = hostPart && !/[*?]/.test(hostPart) ? hostPart : rs[0].domain;
        const path = (slash === -1 ? '/' : pat.slice(slash)).replace(/\*.*$/, '').replace(/\?/g, '') || '/';
        setUrl(`https://${host}${path.startsWith('/') ? path : `/${path}`}`);
      }
    })();
  }, [workerId, navigate, t]);

  useEffect(() => {
    const warn = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const run = useCallback(async () => {
    setRunning(true);
    const res = await cdnService.previewWorker({
      id: worker?.id,
      code,
      request: {
        method,
        url,
        headers: headers.filter((h) => h.k).map((h) => [h.k, h.v]),
        body: ['GET', 'HEAD'].includes(method) ? '' : body,
      },
    });
    setRunning(false);
    if (!res.success) {
      message.error(res.error || t('Test failed'));
      return;
    }
    setResult(res.data);
    if (!res.data.ok) setResultTab('console');
    else if (res.data.outcome === 'respond') setResultTab('body');
  }, [worker, code, method, url, headers, body, t]);

  const deploy = useCallback(async () => {
    setDeploying(true);
    const res = await cdnService.saveWorker({
      id: worker.id, name: worker.name, description: worker.description, code, enabled: worker.enabled,
      message: deployMsg.trim() || undefined,
    });
    setDeploying(false);
    if (res.success) {
      message.success(t('Deployed v{{version}}{{value}}', { version: res.data.version, value: routes.length ? t(', pushing to {{length}} routes', { length: routes.length }) : '' }));
      setWorker((w) => ({ ...w, ...res.data, code }));
      setDeployOpen(false);
      setDeployMsg('');
    } else {
      message.error(res.error || t('Deployment failed'));
    }
  }, [worker, code, deployMsg, routes.length, t]);

  runRef.current = run;
  deployRef.current = () => { if (dirty) setDeployOpen(true); else message.info(t('No undeployed changes')); };

  const onMount = (editor, monaco) => {
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, () => runRef.current());
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => deployRef.current());
  };

  const applyTemplate = (key) => {
    const tpl = WORKER_TEMPLATES.find((t) => t.key === key);
    if (!tpl) return;
    Modal.confirm({
      title: t('Load template "{{label}}"?', { label: t(tpl.label) }),
      content: t('This replaces the code in the editor (not yet deployed; undo with Ctrl/Cmd+Z).'),
      okText: t('Load'),
      cancelText: t('Cancel'),
      onOk: () => setCode(tpl.code),
    });
  };

  const respHeaders = result?.headers || result?.response_headers || [];
  const bodyText = useMemo(() => (result?.body_b64 ? pretty(decodeBody(result.body_b64), result.headers) : ''), [result, t]);

  if (!worker) return <div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>;

  const panelBorder = `1px solid ${token.colorBorderSecondary}`;

  const testPanel = (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div style={{ padding: 12, borderBottom: panelBorder }}>
        <Space.Compact style={{ width: '100%' }}>
          <Select value={method} onChange={setMethod} style={{ width: 104 }} options={METHODS.map((m) => ({ value: m, label: m }))} />
          <Input value={url} onChange={(e) => setUrl(e.target.value)} onPressEnter={run} placeholder="https://example.com/path" />
          <Tooltip title={t('Run (Ctrl/Cmd + Enter)')}>
            <Button type="primary" icon={<CaretRightOutlined />} loading={running} onClick={run}>{t('Send')}</Button>
          </Tooltip>
        </Space.Compact>
        <Tabs
          size="small"
          style={{ marginTop: 4 }}
          items={[
            {
              key: 'headers',
              label: t('Request headers ({{length}})', { length: headers.filter((h) => h.k).length }),
              children: (
                <div style={{ maxHeight: 160, overflow: 'auto' }}>
                  {headers.map((h, i) => (
                    <Space.Compact key={i} style={{ width: '100%', marginBottom: 6 }}>
                      <Input size="small" placeholder={t('Name')} value={h.k} style={{ width: '40%' }}
                        onChange={(e) => setHeaders((hs) => hs.map((x, j) => (j === i ? { ...x, k: e.target.value } : x)))} />
                      <Input size="small" placeholder={t('Value')} value={h.v}
                        onChange={(e) => setHeaders((hs) => hs.map((x, j) => (j === i ? { ...x, v: e.target.value } : x)))} />
                      <Button size="small" icon={<DeleteOutlined />} onClick={() => setHeaders((hs) => hs.filter((_, j) => j !== i))} />
                    </Space.Compact>
                  ))}
                  <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => setHeaders((hs) => [...hs, { k: '', v: '' }])}>{t('Add request header')}</Button>
                </div>
              ),
            },
            {
              key: 'body',
              label: t('Request body'),
              disabled: ['GET', 'HEAD'].includes(method),
              children: <Input.TextArea rows={4} value={body} onChange={(e) => setBody(e.target.value)} placeholder='{"hello":"world"}' style={{ fontFamily: 'monospace' }} />,
            },
          ]}
        />
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 12 }}>
        {!result ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={(
              <span>{t('Click "Send" to process a request with the current code.')}<br />
                <Text type="secondary" style={{ fontSize: 12 }}>{t('Uses the same runtime, variables, and limits as edge nodes; does not affect production.')}</Text>
              </span>
            )}
          />
        ) : (
          <>
            <Space wrap style={{ marginBottom: 8 }}>
              {result.ok
                ? (result.outcome === 'respond'
                  ? <Tag color={result.status >= 400 ? 'red' : result.status >= 300 ? 'orange' : 'green'}>{result.status}</Tag>
                  : <Tag color="blue">{result.outcome === 'continue' ? t('Continue to origin') : t('Rewrite request')}</Tag>)
                : <Tag color="red">{t('Execution error')}</Tag>}
              <Text type="secondary" style={{ fontSize: 12 }}>{result.duration_ms} ms</Text>
            </Space>
            <Tabs
              size="small"
              activeKey={resultTab}
              onChange={setResultTab}
              items={[
                result.ok && result.outcome === 'respond' && {
                  key: 'body',
                  label: t('Response body'),
                  children: (
                    <pre style={{ margin: 0, padding: 12, background: token.colorFillTertiary, borderRadius: token.borderRadius, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 420, overflow: 'auto' }}>
                      {bodyText || <Text type="secondary">{t('(Empty)')}</Text>}
                    </pre>
                  ),
                },
                result.ok && result.outcome !== 'respond' && {
                  key: 'body',
                  label: t('Origin'),
                  children: (
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Alert type="info" showIcon message={result.outcome === 'continue' ? t('The Worker returned no response. The request will continue to the origin.') : t('The Worker rewrote the request and forwarded it to the origin.')} />
                      {result.request && <Text code>{result.request.method} {result.request.url}</Text>}
                      {(result.set_headers || []).length > 0 && <HeaderTable title={t('Set origin request headers')} rows={result.set_headers} />}
                      {(result.remove_headers || []).length > 0 && <Text>{t('Remove origin request header:')}{result.remove_headers.map((h) => <Tag key={h}>{h}</Tag>)}</Text>}
                    </Space>
                  ),
                },
                result.ok && { key: 'headers', label: t('Response headers ({{length}})', { length: respHeaders.length }), children: <HeaderTable rows={respHeaders} /> },
                {
                  key: 'console',
                  label: <Badge size="small" count={(result.logs || []).length + (result.ok ? 0 : 1)} offset={[8, 0]}>{t('Console')}</Badge>,
                  children: (
                    <div style={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {(result.logs || []).map((l, i) => (
                        <div key={i} style={{ padding: '4px 0', borderBottom: `1px dashed ${token.colorBorderSecondary}`, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                          <Tag color={LOG_COLOR[l.level]} style={{ marginRight: 6 }}>{l.level}</Tag>{l.msg}
                        </div>
                      ))}
                      {!result.ok && (
                        <div style={{ padding: '6px 0', color: token.colorError, whiteSpace: 'pre-wrap' }}>
                          <Tag color="red">error</Tag>{result.error}
                        </div>
                      )}
                      {result.ok && !(result.logs || []).length && <Text type="secondary">{t('No console output')}</Text>}
                    </div>
                  ),
                },
              ].filter(Boolean)}
            />
          </>
        )}
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: isWide ? 'calc(100vh - 140px)' : 'auto', minHeight: 520 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <Space wrap>
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(userPath(`/cdn/workers/${worker.id}`))} style={{ paddingLeft: 0 }}>
            {worker.name}
          </Button>
          <Tag>v{worker.version}</Tag>
          {dirty ? <Tag color="orange">{t('Undeployed changes')}</Tag> : <Tag color="success">{t('Already live')}</Tag>}
        </Space>
        <Space>
          <Dropdown
            trigger={['click']}
            menu={{ items: WORKER_TEMPLATES.map((templateItem) => ({ key: templateItem.key, label: t(templateItem.label) })), onClick: ({ key }) => applyTemplate(key) }}
          >
            <Button icon={<SnippetsOutlined />}>{t('Template')}</Button>
          </Dropdown>
          <Tooltip title="Ctrl/Cmd + S">
            <Button type="primary" icon={<RocketOutlined />} disabled={!dirty} onClick={() => setDeployOpen(true)}>{t('Deploy')}</Button>
          </Tooltip>
        </Space>
      </div>

      <div style={{
        flex: 1, minHeight: 0, display: 'flex', flexDirection: isWide ? 'row' : 'column',
        border: panelBorder, borderRadius: token.borderRadiusLG, overflow: 'hidden', background: token.colorBgContainer,
      }}
      >
        <div style={{ flex: isWide ? '1 1 58%' : 'none', height: isWide ? 'auto' : 460, minWidth: 0, borderRight: isWide ? panelBorder : 'none', borderBottom: isWide ? 'none' : panelBorder }}>
          <Editor
            height="100%"
            language="javascript"
            theme={themeMode === 'dark' ? 'vs-dark' : 'light'}
            value={code}
            onChange={(v) => setCode(v ?? '')}
            onMount={onMount}
            options={{ minimap: { enabled: false }, fontSize: 13, scrollBeyondLastLine: false, automaticLayout: true, tabSize: 2 }}
          />
        </div>
        <div style={{ flex: isWide ? '1 1 42%' : 'none', minWidth: 0, minHeight: isWide ? 0 : 420 }}>
          {testPanel}
        </div>
      </div>

      <Modal
        title={t('Deploy a new version')}
        open={deployOpen}
        onCancel={() => setDeployOpen(false)}
        onOk={deploy}
        confirmLoading={deploying}
        okText={t('Deploy v{{value}}', { value: (worker.version || 0) + 1 })}
        cancelText={t('Cancel')}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text type="secondary">
            {routes.length
              ? t('This will be pushed to the edge nodes serving {{length}} routes ({{value}}), usually taking effect within a few seconds.', { length: routes.length, value: [...new Set(routes.map((r) => r.domain))].join('、') })
              : t('This Worker has no routes yet. You can add routes on the overview page after deployment.')}
          </Text>
          <Input.TextArea
            rows={2}
            maxLength={255}
            placeholder={t('Version notes (optional), e.g., Fix CORS headers')}
            value={deployMsg}
            onChange={(e) => setDeployMsg(e.target.value)}
          />
          {!result && <Alert type="warning" showIcon message={t('The current code has not been tested yet. Click Send on the right to test it first.')} />}
          {result && !result.ok && <Alert type="error" showIcon message={t('The last test run failed. Are you sure you want to deploy?')} />}
        </Space>
      </Modal>
    </div>
  );
}

function HeaderTable({ rows, title }) {
  const { t } = useTranslation();
  return (
    <Table
      size="small"
      pagination={false}
      title={title ? () => title : undefined}
      rowKey={(r, i) => `${r[0]}-${i}`}
      dataSource={rows}
      columns={[
        { title: t('Name'), dataIndex: 0, width: '35%', render: (v) => <Text code>{v}</Text> },
        { title: t('Value'), dataIndex: 1, render: (v) => <Text style={{ wordBreak: 'break-all' }}>{v}</Text> },
      ]}
    />
  );
}
